/**
 * Add-contractor dialog for profit-center staff.
 *
 * The Hajoca (Eclipse) account is the source of truth: the user enters only
 * the customer number, the lookup resolves the full prefixed id and the
 * account's name/contact info, and creation confirms that record read-only.
 * Nothing is hand-entered, so the platform org can't drift from the ERP.
 * The server independently re-fetches the account on create — the preview
 * here is informational, not what gets stored.
 */

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { toastApiError } from "@/lib/error-messages";
import {
  createPcContractor,
  lookupPcContractor,
  PC_CONTRACTORS_QUERY_KEY,
} from "@/requests/pc-contractors";
import type { PcContractorLookupResult } from "@onsemble/shared/types/pc-contractors";

const lookupFormSchema = z.object({
  hajocaCustomerId: z
    .string()
    .min(1, "Customer number is required")
    .regex(
      /^[A-Za-z]?\d+$/,
      "Enter the customer number (the region letter is added automatically)",
    ),
});

type LookupFormData = z.infer<typeof lookupFormSchema>;

interface PcAddContractorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PcAddContractorDialog({
  open,
  onOpenChange,
}: PcAddContractorDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Step 1: customer number -> lookup. Step 2: confirm the ERP record.
  const [lookup, setLookup] = useState<PcContractorLookupResult | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const form = useForm<LookupFormData>({
    resolver: zodResolver(lookupFormSchema),
    defaultValues: { hajocaCustomerId: "" },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({ hajocaCustomerId: "" });
    setLookup(null);
    setLookupError(null);
  }, [open, form]);

  const lookupMutation = useMutation({
    mutationFn: (customerNumber: string) => lookupPcContractor(customerNumber),
    onSuccess: (result) => {
      if (!result.account) {
        // Creation requires the ERP record — no manual fallback by design
        setLookupError(
          t("pages.pcContractors.notFoundInErp", {
            customerId: result.customerId,
          }),
        );
        return;
      }
      setLookupError(null);
      setLookup(result);
    },
    onError: (error) => {
      toastApiError(toast, error, t("pages.pcContractors.lookupFailed"));
    },
  });

  const handleLookup = async () => {
    const isValid = await form.trigger("hajocaCustomerId");
    if (!isValid) return;
    lookupMutation.mutate(form.getValues("hajocaCustomerId"));
  };

  const createMutation = useMutation({
    mutationFn: (customerId: string) =>
      createPcContractor({ hajocaCustomerId: customerId }),
    onSuccess: (result) => {
      toast({
        title: t("pages.pcContractors.createdToast"),
        description: result.clerkOrgName,
      });
      queryClient.invalidateQueries({ queryKey: PC_CONTRACTORS_QUERY_KEY });
      onOpenChange(false);
    },
    onError: (error) => {
      toastApiError(toast, error, t("pages.pcContractors.createFailed"));
    },
  });

  const account = lookup?.account;
  const accountAddress = account
    ? [
        account.address.street,
        [account.address.city, account.address.state]
          .filter(Boolean)
          .join(", "),
        account.address.zipCode,
      ]
        .filter(Boolean)
        .join(", ")
    : "";
  const isPending = lookupMutation.isPending || createMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("pages.pcContractors.createTitle")}</DialogTitle>
          <DialogDescription>
            {t("pages.pcContractors.createDescription")}
          </DialogDescription>
        </DialogHeader>

        {!lookup ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="hajocaCustomerId">
                {t("pages.pcContractors.fields.customerNumber")}
              </Label>
              <Input
                id="hajocaCustomerId"
                {...form.register("hajocaCustomerId")}
                placeholder="181946"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleLookup();
                  }
                }}
              />
              {form.formState.errors.hajocaCustomerId && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.hajocaCustomerId.message}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {t("pages.pcContractors.customerNumberHint")}
              </p>
            </div>

            {lookupError && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{lookupError}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="button" onClick={handleLookup} disabled={isPending}>
                {lookupMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t("pages.pcContractors.lookupAndContinue")}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Read-only ERP record — what the contractor account is created from */}
            <div className="rounded-md border bg-muted/50 px-4 py-3 text-sm space-y-1">
              <div className="font-medium text-base">{account!.name}</div>
              <div className="text-muted-foreground">
                {t("pages.pcContractors.columns.customerId")}:{" "}
                <span className="font-mono">{lookup.customerId}</span>
              </div>
              {account!.phone && (
                <div className="text-muted-foreground">{account!.phone}</div>
              )}
              {accountAddress && (
                <div className="text-muted-foreground">{accountAddress}</div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("pages.pcContractors.confirmFromErp")}
            </p>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setLookup(null)}
                disabled={isPending}
              >
                {t("common.back")}
              </Button>
              <Button
                type="button"
                onClick={() => createMutation.mutate(lookup.customerId)}
                disabled={isPending}
              >
                {createMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t("pages.pcContractors.createButton")}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
